/**
 * React Query hooks for the Peersona Switchboard admin API
 * (`/api/v3/peersona_admin/...`). Backing UI is KINDLY-955.
 */
import axios from 'axios';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { axiosDelete, axiosGet, axiosPost, axiosPut } from '../api/axios';

// ---------- Types ----------

export type ChatBotStatus = 'on' | 'off';
export type PersonaStatus = ChatBotStatus | string;
export type PersonaProcessingStatus = 'pending' | 'processing' | 'ready' | 'failed' | string;

export interface LastEvalSummary {
  id: number;
  status: string;
  label?: string | null;
  total_flags?: number | null;
  total_scenarios?: number | null;
  total_turns?: number | null;
  finished_at?: string | null;
}

export interface ProcessingProgress {
  stage?: string;
  total?: number;
  completed?: number;
  message?: string | null;
  error?: string | null;
  started_at?: string | null;
  updated_at?: string | null;
}

export interface Persona {
  id: number;
  name: string;
  display_name?: string | null;
  listener_role_id: number | null;
  status: PersonaStatus;
  processing_status?: PersonaProcessingStatus;
  processing_progress?: ProcessingProgress | null;
  chunk_count?: number;
  assigned_client_count?: number;
  welcome_message?: string | null;
  scope_line?: string | null;
  avatar_file_path?: string | null;
  image_url?: string | null;
  welcome_video_vimeo_id?: string | null;
  active_prompt_version_id?: number | null;
  active_prompt_version?: number | null;
  last_eval_summary?: LastEvalSummary | null;
  created_at?: string;
  profile_prompt?: string | null;
  prompt?: string | null;
  corpus_by_source?: Array<{ source_type: string; count: number }>;
  assigned_client_ids?: number[];
}

export interface PersonaListResponse {
  data: Persona[];
  count: number;
}

export interface CreatePersonaPayload {
  name: string;
  listener_role_id: number;
  display_name?: string | null;
  welcome_message?: string;
  scope_line?: string;
  profile_prompt?: string;
  avatar_file_path?: string;
  welcome_video_vimeo_id?: string;
  prompt?: string;
}

export interface UpdatePersonaPayload {
  name?: string;
  display_name?: string | null;
  welcome_message?: string;
  scope_line?: string;
  avatar_file_path?: string;
  welcome_video_vimeo_id?: string;
  prompt?: string;
}

export interface CorpusChunk {
  id: number;
  chat_bot_id: number;
  source_type?: string | null;
  source_ref_id?: number | null;
  content: string;
  content_preview?: string | null;
  chunk_metadata?: Record<string, unknown> | null;
  created_at?: string;
}

export interface CorpusChunksResponse {
  data: CorpusChunk[];
  count: number;
  processing_status?: string | null;
}

export interface AssignedClient {
  id: number;
  name: string;
  status: string;
}

export interface PersonaClientsResponse {
  chatbot_id: number;
  clients: AssignedClient[];
  client_ids: number[];
}

// Rule types
export interface RuleTurn {
  text: string;
  precondition?: string | null;
  [key: string]: unknown;
}

export type RuleScope = 'persona' | 'shared';

export interface Rule {
  id: number;
  chatbot_id?: number | null;
  shared: boolean;
  slug: string;
  notes?: string | null;
  tags?: string[];
  turns: RuleTurn[];
  expected_tier?: string | null;
  expected_intent?: string | null;
  expected_concierge_subtype?: string | null;
  expected_handoff_target?: string | null;
  kind?: string;
  enabled: boolean;
  source?: string;
  source_thread_ref?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface RulePayload {
  slug: string;
  notes?: string;
  tags?: string[];
  turns: RuleTurn[];
  expected_tier?: string;
  expected_intent?: string;
  expected_concierge_subtype?: string;
  expected_handoff_target?: string;
  chatbot_id?: number | null;
  kind?: string;
  enabled?: boolean;
}

// Eval types
export type EvalRunStatus = 'queued' | 'running' | 'complete' | 'failed' | 'cancelled' | string;

export interface EvalFlag {
  name: string;
  description?: string;
}

export interface EvalScenarioTurn {
  user_message: string;
  ai_response?: string;
  tier?: string;
  intent?: string;
  flags?: Array<EvalFlag | string>;
  error?: string | null;
  skipped?: boolean;
  skip_reason?: string | null;
  [key: string]: unknown;
}

export interface EvalScenarioResult {
  id: string;
  notes?: string | null;
  tags?: string[];
  expected_tier?: string | null;
  expected_intent?: string | null;
  expected_concierge_subtype?: string | null;
  expected_handoff_target?: string | null;
  turns?: EvalScenarioTurn[];
  scenario_flags?: Array<EvalFlag | string>;
  flag_count?: number;
  [key: string]: unknown;
}

export interface EvalRun {
  id: number;
  chatbot_id: number;
  label?: string | null;
  status: EvalRunStatus;
  rule_ids?: number[];
  prompt_version_id?: number | null;
  error?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  heartbeat_at?: string | null;
  created_at?: string;
  summary?: Record<string, unknown> | null;
  results?: { scenarios?: EvalScenarioResult[]; [key: string]: unknown } | null;
  proposal?: EvalProposal | null;
}

export interface EvalProposalEdit {
  field: string;
  proposed: string;
  rationale?: string;
}

export interface EvalProposalDiff {
  field: string;
  diff?: string;
  left?: string;
  right?: string;
}

export interface EvalProposal {
  requested?: boolean;
  status?: 'pending' | 'generating' | 'ready' | 'empty' | 'failed' | string;
  summary?: string;
  error?: string;
  edits?: EvalProposalEdit[];
  diffs?: EvalProposalDiff[];
  baseline?: Record<string, string>;
  failure_count?: number;
  [key: string]: unknown;
}

export interface AcceptEvalProposalPayload {
  accept_fields: string[];
  change_note?: string;
}

export interface EvalRunListResponse {
  data: EvalRun[];
  count: number;
}

export interface CreateEvalRunPayload {
  rule_ids?: number[];
  tags?: string[];
  label?: string;
  prompt_version_id?: number;
  propose_prompt_changes?: boolean;
}

// Prompt types
export type PromptVersionStatus = 'draft' | 'active' | 'archived' | string;

export interface PromptVersion {
  id: number;
  chatbot_id: number;
  version: number;
  status: PromptVersionStatus;
  profile_prompt: string;
  scope_line?: string | null;
  welcome_message?: string | null;
  template_overrides?: Record<string, unknown>;
  change_note?: string | null;
  source_eval_run_id?: number | null;
  created_at?: string;
}

export interface PromptVersionListResponse {
  data: PromptVersion[];
  count: number;
}

export interface PromptFieldDiff {
  field: string;
  diff: string;
  left?: string | null;
  right?: string | null;
}

export interface PromptDiffResponse {
  left_version_id: number;
  right_version_id: number;
  fields: PromptFieldDiff[];
}

export interface CreatePromptVersionPayload {
  from_version_id?: number | null;
  source_eval_run_id?: number | null;
  profile_prompt?: string | null;
  scope_line?: string | null;
  welcome_message?: string | null;
  template_overrides?: Record<string, unknown>;
  change_note?: string | null;
}

export interface UpdatePromptVersionPayload {
  profile_prompt?: string;
  scope_line?: string | null;
  welcome_message?: string | null;
  template_overrides?: Record<string, unknown>;
  change_note?: string | null;
}

// Sandbox types
export interface SandboxMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  peersona?: SandboxPeersonaMetadata | null;
}

export interface SandboxPeersonaMetadata {
  tier?: string | null;
  intent?: string | null;
  top_score?: number | null;
  handoff?: Record<string, unknown> | null;
  pending_handoff?: Record<string, unknown> | null;
  pending_referral?: Record<string, unknown> | null;
  referral?: Record<string, unknown> | null;
  pending_disposition?: string | null;
  [key: string]: unknown;
}

export interface SandboxChatRequest {
  message: string;
  history: SandboxMessage[];
  prompt_version_id?: number;
}

export interface SandboxToolResult {
  tool: string;
  result: SandboxPeersonaMetadata;
}

export interface SandboxChatResponse {
  ai_response: string;
  tool_results?: SandboxToolResult[];
  crisis?: Record<string, unknown> | null;
  chatbot_id?: number;
  prompt_version_id?: number | null;
}

export interface PromoteSandboxPayload {
  turns: RuleTurn[];
  slug?: string;
  notes?: string;
  tags?: string[];
  expected_tier?: string;
  expected_intent?: string;
  expected_concierge_subtype?: string;
  expected_handoff_target?: string;
}

// ---------- Query keys ----------

export const personaAdminKeys = {
  personas: ['personaAdmin', 'personas'] as const,
  persona: (id: number | string) => ['personaAdmin', 'persona', String(id)] as const,
  corpus: (id: number | string, page: number, perPage: number) =>
    ['personaAdmin', 'corpus', String(id), page, perPage] as const,
  clients: (id: number | string) => ['personaAdmin', 'clients', String(id)] as const,
  rules: (id: number | string, includeShared: boolean) => ['personaAdmin', 'rules', String(id), includeShared] as const,
  sharedRules: ['personaAdmin', 'sharedRules'] as const,
  evalRuns: (id: number | string) => ['personaAdmin', 'evalRuns', String(id)] as const,
  evalRun: (runId: number | string) => ['personaAdmin', 'evalRun', String(runId)] as const,
  promptVersions: (id: number | string) => ['personaAdmin', 'promptVersions', String(id)] as const,
  promptDiff: (vid: number | string, against: number | string | undefined) =>
    ['personaAdmin', 'promptDiff', String(vid), String(against ?? '')] as const,
};

// ---------- API functions ----------

const listPersonas = async (): Promise<PersonaListResponse> => {
  const { data } = await axiosGet('/peersona_admin/personas', {}, 'v3');
  return data;
};

const getPersona = async (id: number | string): Promise<Persona> => {
  const { data } = await axiosGet(`/peersona_admin/personas/${id}`, {}, 'v3');
  return data;
};

const createPersona = async (payload: CreatePersonaPayload): Promise<Persona> => {
  const { data } = await axiosPost('/peersona_admin/personas', payload, 'v3');
  return data;
};

const updatePersona = async (id: number | string, payload: UpdatePersonaPayload): Promise<Persona> => {
  const { data } = await axiosPut(`/peersona_admin/personas/${id}`, payload, 'v3');
  return data;
};

const publishPersona = async (id: number | string): Promise<Persona> => {
  const { data } = await axiosPost(`/peersona_admin/personas/${id}/publish`, {}, 'v3');
  return data;
};

const unpublishPersona = async (id: number | string): Promise<Persona> => {
  const { data } = await axiosPost(`/peersona_admin/personas/${id}/unpublish`, {}, 'v3');
  return data;
};

const uploadCorpus = async (id: number | string, file: File): Promise<{ chunk_count?: number }> => {
  const domain = process.env.REACT_APP_DOMAIN_NAME;
  const apiVersion3 = process.env.REACT_APP_API_VERSION3;
  const url = `${domain}${apiVersion3}/peersona_admin/personas/${id}/corpus`;
  const formData = new FormData();
  formData.append('file', file, file.name);
  const token = localStorage.getItem('authorization_token');
  const response = await axios.post(url, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
    },
  });
  return response.data;
};

const listCorpus = async (id: number | string, page: number, perPage: number): Promise<CorpusChunksResponse> => {
  const { data } = await axiosGet(`/peersona_admin/personas/${id}/corpus`, { page, per_page: perPage }, 'v3');
  return data;
};

const deleteCorpusChunk = async (id: number | string, chunkId: number | string): Promise<void> => {
  await axiosDelete(`/peersona_admin/personas/${id}/corpus/${chunkId}`, {}, 'v3');
};

const getPersonaClients = async (id: number | string): Promise<PersonaClientsResponse> => {
  const { data } = await axiosGet(`/peersona_admin/personas/${id}/clients`, {}, 'v3');
  return data;
};

const putPersonaClients = async (id: number | string, clientIds: number[]): Promise<PersonaClientsResponse> => {
  const { data } = await axiosPut(`/peersona_admin/personas/${id}/clients`, { client_ids: clientIds }, 'v3');
  return data;
};

const listRules = async (id: number | string, includeShared: boolean): Promise<RuleListResponse> => {
  const { data } = await axiosGet(`/peersona_admin/personas/${id}/rules`, { include_shared: includeShared }, 'v3');
  return data;
};

const createPersonaRule = async (id: number | string, payload: RulePayload): Promise<Rule> => {
  const { data } = await axiosPost(`/peersona_admin/personas/${id}/rules`, payload, 'v3');
  return data;
};

const listSharedRules = async (): Promise<RuleListResponse> => {
  const { data } = await axiosGet('/peersona_admin/rules/shared', {}, 'v3');
  return data;
};

const createSharedRule = async (payload: RulePayload): Promise<Rule> => {
  const { data } = await axiosPost('/peersona_admin/rules/shared', payload, 'v3');
  return data;
};

const updateRule = async (ruleId: number | string, payload: Partial<RulePayload>): Promise<Rule> => {
  const { data } = await axiosPut(`/peersona_admin/rules/${ruleId}`, payload, 'v3');
  return data;
};

const deleteRule = async (ruleId: number | string): Promise<void> => {
  await axiosDelete(`/peersona_admin/rules/${ruleId}`, {}, 'v3');
};

const createEvalRun = async (id: number | string, payload: CreateEvalRunPayload): Promise<EvalRun> => {
  const { data } = await axiosPost(`/peersona_admin/personas/${id}/eval_runs`, payload, 'v3');
  return data;
};

const listEvalRuns = async (id: number | string): Promise<EvalRunListResponse> => {
  const { data } = await axiosGet(`/peersona_admin/personas/${id}/eval_runs`, {}, 'v3');
  return data;
};

const getEvalRun = async (runId: number | string): Promise<EvalRun> => {
  const { data } = await axiosGet(`/peersona_admin/eval_runs/${runId}`, {}, 'v3');
  return data;
};

const cancelEvalRun = async (runId: number | string): Promise<EvalRun> => {
  const { data } = await axiosPost(`/peersona_admin/eval_runs/${runId}/cancel`, {}, 'v3');
  return data;
};

const acceptEvalProposal = async (
  runId: number | string,
  payload: AcceptEvalProposalPayload,
): Promise<PromptVersion> => {
  const { data } = await axiosPost(`/peersona_admin/eval_runs/${runId}/proposal/accept`, payload, 'v3');
  return data;
};

const listPromptVersions = async (id: number | string): Promise<PromptVersionListResponse> => {
  const { data } = await axiosGet(`/peersona_admin/personas/${id}/prompt_versions`, {}, 'v3');
  return data;
};

const createPromptVersion = async (
  id: number | string,
  payload: CreatePromptVersionPayload,
): Promise<PromptVersion> => {
  const { data } = await axiosPost(`/peersona_admin/personas/${id}/prompt_versions`, payload, 'v3');
  return data;
};

const updatePromptVersion = async (
  vid: number | string,
  payload: UpdatePromptVersionPayload,
): Promise<PromptVersion> => {
  const { data } = await axiosPut(`/peersona_admin/prompt_versions/${vid}`, payload, 'v3');
  return data;
};

const activatePromptVersion = async (
  vid: number | string,
  payload: { change_note?: string } = {},
): Promise<PromptVersion> => {
  const { data } = await axiosPost(`/peersona_admin/prompt_versions/${vid}/activate`, payload, 'v3');
  return data;
};

const generatePromptVersion = async (
  id: number | string,
  payload: { include_rules?: boolean; activate?: boolean; change_note?: string } = {},
): Promise<PromptVersion> => {
  const { data } = await axiosPost(`/peersona_admin/personas/${id}/prompt_versions/generate`, payload, 'v3');
  return data;
};

const deletePromptVersion = async (vid: number | string): Promise<void> => {
  await axiosDelete(`/peersona_admin/prompt_versions/${vid}`, {}, 'v3');
};

const getPromptDiff = async (vid: number | string, against?: number | string): Promise<PromptDiffResponse> => {
  const { data } = await axiosGet(
    `/peersona_admin/prompt_versions/${vid}/diff`,
    against !== undefined ? { against } : {},
    'v3',
  );
  return data;
};

const sandboxChat = async (id: number | string, payload: SandboxChatRequest): Promise<SandboxChatResponse> => {
  const { data } = await axiosPost(`/peersona_admin/personas/${id}/sandbox/chat`, payload, 'v3');
  return data;
};

const promoteSandbox = async (id: number | string, payload: PromoteSandboxPayload): Promise<Rule> => {
  const { data } = await axiosPost(`/peersona_admin/personas/${id}/sandbox/promote`, payload, 'v3');
  return data;
};

// ---------- Hooks ----------

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.description || error?.response?.data?.message || error?.message || fallback;

export const usePersonas = (enabled: boolean = true) =>
  useQuery<PersonaListResponse>(personaAdminKeys.personas, listPersonas, { enabled });

export const usePersona = (id?: number | string, options?: { refetchInterval?: number | false }) =>
  useQuery<Persona>(personaAdminKeys.persona(id ?? ''), () => getPersona(id!), {
    enabled: id !== undefined && id !== null && id !== '',
    refetchInterval: options?.refetchInterval,
  });

export const useCreatePersona = () => {
  const queryClient = useQueryClient();
  return useMutation((payload: CreatePersonaPayload) => createPersona(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(personaAdminKeys.personas);
      toast.success('Persona created');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to create persona')),
  });
};

export const useUpdatePersona = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation((payload: UpdatePersonaPayload) => updatePersona(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(personaAdminKeys.persona(id));
      queryClient.invalidateQueries(personaAdminKeys.personas);
      toast.success('Persona updated');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to update persona')),
  });
};

export const usePublishPersona = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation(() => publishPersona(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(personaAdminKeys.persona(id));
      queryClient.invalidateQueries(personaAdminKeys.personas);
      toast.success('Persona published');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to publish persona')),
  });
};

export const useUnpublishPersona = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation(() => unpublishPersona(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(personaAdminKeys.persona(id));
      queryClient.invalidateQueries(personaAdminKeys.personas);
      toast.success('Persona unpublished');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to unpublish persona')),
  });
};

export const useUploadCorpus = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation((file: File) => uploadCorpus(id, file), {
    onSuccess: () => {
      queryClient.invalidateQueries(['personaAdmin', 'corpus', String(id)]);
      queryClient.invalidateQueries(personaAdminKeys.persona(id));
      toast.success('Corpus upload accepted');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Corpus upload failed')),
  });
};

export const useCorpusChunks = (
  id: number | string,
  page: number = 1,
  perPage: number = 25,
  options?: { refetchInterval?: number | false },
) =>
  useQuery<CorpusChunksResponse>(personaAdminKeys.corpus(id, page, perPage), () => listCorpus(id, page, perPage), {
    keepPreviousData: true,
    enabled: id !== undefined && id !== null && id !== '',
    refetchInterval: options?.refetchInterval,
  });

export const useDeleteCorpusChunk = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation((chunkId: number | string) => deleteCorpusChunk(id, chunkId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['personaAdmin', 'corpus', String(id)]);
      queryClient.invalidateQueries(personaAdminKeys.persona(id));
      toast.success('Chunk deleted');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to delete chunk')),
  });
};

export const usePersonaClients = (id?: number | string) =>
  useQuery<PersonaClientsResponse>(personaAdminKeys.clients(id ?? ''), () => getPersonaClients(id!), {
    enabled: id !== undefined && id !== null && id !== '',
  });

export const useUpdatePersonaClients = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation((clientIds: number[]) => putPersonaClients(id, clientIds), {
    onSuccess: () => {
      queryClient.invalidateQueries(personaAdminKeys.clients(id));
      queryClient.invalidateQueries(personaAdminKeys.persona(id));
      toast.success('Client assignments saved');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to save client assignments')),
  });
};

export const usePersonaRules = (id?: number | string, includeShared: boolean = true) =>
  useQuery<RuleListResponse>(personaAdminKeys.rules(id ?? '', includeShared), () => listRules(id!, includeShared), {
    enabled: id !== undefined && id !== null && id !== '',
  });

export const useCreatePersonaRule = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation((payload: RulePayload) => createPersonaRule(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['personaAdmin', 'rules', String(id)]);
      toast.success('Rule created');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to create rule')),
  });
};

export const useSharedRules = (enabled: boolean = true) =>
  useQuery<RuleListResponse>(personaAdminKeys.sharedRules, listSharedRules, { enabled });

export const useCreateSharedRule = () => {
  const queryClient = useQueryClient();
  return useMutation((payload: RulePayload) => createSharedRule(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(personaAdminKeys.sharedRules);
      queryClient.invalidateQueries(['personaAdmin', 'rules']);
      toast.success('Shared rule created');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to create shared rule')),
  });
};

export const useUpdateRule = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ ruleId, payload }: { ruleId: number; payload: Partial<RulePayload> }) => updateRule(ruleId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['personaAdmin', 'rules']);
        queryClient.invalidateQueries(personaAdminKeys.sharedRules);
        toast.success('Rule updated');
      },
      onError: (error: any) => toast.error(errorMessage(error, 'Failed to update rule')),
    },
  );
};

export const useDeleteRule = () => {
  const queryClient = useQueryClient();
  return useMutation((ruleId: number) => deleteRule(ruleId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['personaAdmin', 'rules']);
      queryClient.invalidateQueries(personaAdminKeys.sharedRules);
      toast.success('Rule deleted');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to delete rule')),
  });
};

export const useCreateEvalRun = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation((payload: CreateEvalRunPayload) => createEvalRun(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(personaAdminKeys.evalRuns(id));
      toast.success('Eval run started');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to start eval run')),
  });
};

export const usePersonaEvalRuns = (id?: number | string) =>
  useQuery<EvalRunListResponse>(personaAdminKeys.evalRuns(id ?? ''), () => listEvalRuns(id!), {
    enabled: id !== undefined && id !== null && id !== '',
  });

export const useEvalRun = (runId?: number | string, options?: Partial<UseQueryOptions<EvalRun>>) =>
  useQuery<EvalRun>(personaAdminKeys.evalRun(runId ?? ''), () => getEvalRun(runId!), {
    enabled: runId !== undefined && runId !== null && runId !== '',
    ...options,
  });

export const useCancelEvalRun = () => {
  const queryClient = useQueryClient();
  return useMutation((runId: number) => cancelEvalRun(runId), {
    onSuccess: (run) => {
      queryClient.invalidateQueries(personaAdminKeys.evalRun(run.id));
      queryClient.invalidateQueries(personaAdminKeys.evalRuns(run.chatbot_id));
      toast.success('Eval run cancelled');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to cancel eval run')),
  });
};

export const useAcceptEvalProposal = (personaId: number | string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ runId, payload }: { runId: number; payload: AcceptEvalProposalPayload }) => acceptEvalProposal(runId, payload),
    {
      onSuccess: (version, variables) => {
        queryClient.invalidateQueries(personaAdminKeys.promptVersions(personaId));
        queryClient.invalidateQueries(personaAdminKeys.evalRun(variables.runId));
        toast.success(`Created draft v${version.version} from proposal`);
      },
      onError: (error: any) => toast.error(errorMessage(error, 'Failed to create draft from proposal')),
    },
  );
};

export const usePromptVersions = (id?: number | string) =>
  useQuery<PromptVersionListResponse>(personaAdminKeys.promptVersions(id ?? ''), () => listPromptVersions(id!), {
    enabled: id !== undefined && id !== null && id !== '',
  });

export const useCreatePromptVersion = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation((payload: CreatePromptVersionPayload) => createPromptVersion(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(personaAdminKeys.promptVersions(id));
      toast.success('Prompt version saved');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to save prompt version')),
  });
};

export const useUpdatePromptVersion = (personaId: number | string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ vid, payload }: { vid: number; payload: UpdatePromptVersionPayload }) => updatePromptVersion(vid, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(personaAdminKeys.promptVersions(personaId));
        toast.success('Prompt version updated');
      },
      onError: (error: any) => toast.error(errorMessage(error, 'Failed to update prompt version')),
    },
  );
};

export const useActivatePromptVersion = (personaId: number | string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ vid, change_note }: { vid: number; change_note?: string }) => activatePromptVersion(vid, { change_note }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(personaAdminKeys.promptVersions(personaId));
        queryClient.invalidateQueries(personaAdminKeys.persona(personaId));
        toast.success('Prompt version activated');
      },
      onError: (error: any) => toast.error(errorMessage(error, 'Failed to activate prompt version')),
    },
  );
};

export const useGeneratePromptVersion = (personaId: number | string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: { include_rules?: boolean; activate?: boolean; change_note?: string } = {}) =>
      generatePromptVersion(personaId, payload),
    {
      onSuccess: (version) => {
        queryClient.invalidateQueries(personaAdminKeys.promptVersions(personaId));
        queryClient.invalidateQueries(personaAdminKeys.persona(personaId));
        toast.success(
          version.status === 'active'
            ? `Generated and activated v${version.version}`
            : `Generated draft v${version.version}`,
        );
      },
      onError: (error: any) => toast.error(errorMessage(error, 'Failed to generate prompt')),
    },
  );
};

export const useDeletePromptVersion = (personaId: number | string) => {
  const queryClient = useQueryClient();
  return useMutation((vid: number) => deletePromptVersion(vid), {
    onSuccess: () => {
      queryClient.invalidateQueries(personaAdminKeys.promptVersions(personaId));
      toast.success('Prompt version deleted');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to delete prompt version')),
  });
};

export const usePromptDiff = (vid?: number | string, against?: number | string) =>
  useQuery<PromptDiffResponse>(personaAdminKeys.promptDiff(vid ?? '', against), () => getPromptDiff(vid!, against), {
    enabled: vid !== undefined && vid !== null && vid !== '',
  });

export const useSandboxChat = (id: number | string) =>
  useMutation((payload: SandboxChatRequest) => sandboxChat(id, payload), {
    onError: (error: any) => toast.error(errorMessage(error, 'Sandbox chat failed')),
  });

export const usePromoteSandbox = (id: number | string) => {
  const queryClient = useQueryClient();
  return useMutation((payload: PromoteSandboxPayload) => promoteSandbox(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['personaAdmin', 'rules', String(id)]);
      toast.success('Sandbox turns promoted to rule');
    },
    onError: (error: any) => toast.error(errorMessage(error, 'Failed to promote sandbox turns')),
  });
};

/** Extract PeersonaResponse metadata from the tool_results envelope. */
export function extractSandboxMetadata(response: SandboxChatResponse): SandboxPeersonaMetadata | null {
  const pr = response.tool_results?.find((tr) => tr.tool === 'peersona_retrieval');
  return pr?.result ?? null;
}

/** Derive a UI-friendly scope label from a Rule's backend fields. */
export function ruleScope(rule: Pick<Rule, 'shared' | 'chatbot_id'>): RuleScope {
  return rule.shared || rule.chatbot_id == null ? 'shared' : 'persona';
}

export interface RuleListResponse {
  data: Rule[];
  count: number;
}
